import { Country } from '@angular-material-extensions/select-country';
import { Component, EventEmitter, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { regexHelper } from '@helpers';
import { generalHelpers } from '@mzima-client/sdk';
import { AuthService, EnvService } from '@services';
import { Subject, takeUntil, throttleTime } from 'rxjs';
import { countryUS } from '../../../core/helpers/countries';
import {
  emailExistsValidator,
  otpValidator,
  phoneExistsValidator,
  rateLimitValidator,
} from '../../../core/validators';

@Component({
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss'],
})
export class RegistrationFormComponent {
  @Output() registered = new EventEmitter();
  public isPasswordVisible = false;
  public isVerifyOtpVisible = false;
  public form: FormGroup;
  public submitted = false;
  public authType = '';
  public selectedCountry = countryUS;
  public registrationError: string;
  public otpDeadline: Date;
  public otpMinutes: number = 3;
  public isCountdownEnded = false;

  private otpClick$ = new Subject<void>();
  private verifyOtpClick$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private env: EnvService,
    private formBuilder: FormBuilder,
  ) {
    this.authType = this.env.environment.auth_type;

    const passwordAuthValidator =
      this.authType === 'password' ? Validators.required : Validators.nullValidator;
    const phoneAuthValidator =
      this.authType === 'phone' ? Validators.required : Validators.nullValidator;
    const otpAuthValidator =
      this.authType === 'phone' && this.isVerifyOtpVisible
        ? Validators.required
        : Validators.nullValidator;

    this.form = this.formBuilder.group({
      name: ['', [passwordAuthValidator]],
      email: ['', [passwordAuthValidator, Validators.pattern(regexHelper.emailValidate())]],
      password: [
        '',
        [
          passwordAuthValidator,
          Validators.minLength(generalHelpers.CONST.MIN_PASSWORD_LENGTH),
          Validators.maxLength(generalHelpers.CONST.MAX_PASSWORD_LENGTH),
        ],
      ],
      agreement: [false, [Validators.required]],
      phone: [
        '',
        [phoneAuthValidator, rateLimitValidator, Validators.pattern(regexHelper.phoneValidate())],
      ],
      otp: ['', [otpAuthValidator, Validators.pattern(regexHelper.otpValidate())]],
    });

    this.otpClick$
      .pipe(takeUntil(this.destroy$), throttleTime(500))
      .subscribe(() => this.handleOtpRequest());

    this.verifyOtpClick$
      .pipe(takeUntil(this.destroy$), throttleTime(500))
      .subscribe(() => this.handleVerifyOtp());
  }

  public onCountrySelected(country: Country) {
    this.selectedCountry = country;
  }

  getErrorMessage(field: string) {
    switch (field) {
      case 'name':
      case 'agreement':
        return this.authService.getControlError(this.form, field, ['required']);
      case 'email':
        return this.authService.getControlError(this.form, field, [
          'required',
          'pattern',
          'emailExists',
        ]);
      case 'password':
        return this.authService.getControlError(this.form, field, [
          'required',
          'minlength',
          'maxlength',
        ]);
      case 'phone':
        return this.authService.getControlError(this.form, field, [
          'required',
          'pattern',
          'phoneExists',
          'blocked',
        ]);
      case 'otp':
        return this.authService.getControlError(this.form, field, ['pattern', 'invalid']);
    }
  }

  togglePasswordVisible() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  goBack() {
    this.toggleOtpVerify(false);
    this.form.reset();
    this.enableForm();
  }

  toggleOtpVerify(value: boolean) {
    this.isVerifyOtpVisible = value;
  }

  enableForm() {
    this.submitted = false;
    this.form.enable();
  }

  // signup() {
  //   const { email, password, name, phone } = this.form.value;
  //   this.form.disable();
  //   this.submitted = true;
  //   this.checkExistEmailError(false);

  //   this.authService.signup(email, password, name, phone).subscribe({
  //     next: (response) => {
  //       this.authService.login(email, password).subscribe({
  //         next: () => {
  //           this.registered.emit(response);
  //         },
  //       });
  //     },
  //     error: ({ error }) => {
  //       this.enableForm();
  //       if (error.errors[1].message === 'email is already in use') {
  //         this.checkExistEmailError(true);
  //       }
  //     },
  //   });
  // }

  requestOtp() {
    this.form.disable();
    this.submitted = true;
    this.otpClick$.next();
  }

  handleOtpRequest() {
    const { phone } = this.form.value;
    this.registrationError = '';

    this.authService.requestOtp(phone, this.selectedCountry.numericCode, 'registration').subscribe({
      next: () => {
        this.toggleOtpVerify(true);
        this.checkExistPhoneError(false);
        this.form.enable();
        this.submitted = false;
        this.otpDeadline = new Date(new Date().getTime() + this.otpMinutes * 60000);
        this.isCountdownEnded = false;
      },
      error: (err) => {
        this.enableForm();
        if (err.status === 400) {
          this.checkExistPhoneError(true);
          setTimeout(() => {
            this.checkExistPhoneError(false);
          }, 4000);
        } else if (
          err.status === 500 &&
          err.error.message ===
            `Please wait ${this.otpMinutes} minutes before requesting another code for the same phone number.`
        ) {
          this.checkRateLimitError(true);
          setTimeout(() => {
            this.checkRateLimitError(false);
          }, 4000);
        } else {
          this.registrationError = err.error.message;
        }
      },
    });
  }

  onOtpCountdownEnd() {
    this.isCountdownEnded = true;
  }

  verifyOtp() {
    this.form.disable();
    this.submitted = true;
    this.verifyOtpClick$.next();
  }

  handleVerifyOtp() {
    const { email, password, name, phone, otp } = this.form.value;
    this.authService
      .verifyOtpAndSignup(email, password, name, phone, this.selectedCountry.numericCode, otp)
      .subscribe({
        next: (response) => {
          this.registered.emit(response);
        },
        error: (err) => {
          this.enableForm();

          if (err.status === 400) {
            if (err.error.message === 'Invalid OTP') {
              this.checkValidOtpError(true);
              setTimeout(() => {
                this.checkValidOtpError(false);
              }, 4000);
            } else {
              this.checkExistPhoneError(true);
              setTimeout(() => {
                this.checkExistPhoneError(false);
              }, 4000);
            }
          } else {
            this.registrationError = err.error.message;
          }
        },
      });
  }

  private checkExistEmailError(isExists: boolean) {
    this.setFieldsValidators([this.form.controls['email']], [emailExistsValidator(isExists)]);
    this.getErrorMessage('email');
  }

  private checkRateLimitError(blocked: boolean) {
    this.setFieldsValidators([this.form.controls['phone']], [rateLimitValidator(blocked)]);
    this.getErrorMessage('phone');
  }

  private checkExistPhoneError(isExists: boolean) {
    this.setFieldsValidators([this.form.controls['phone']], [phoneExistsValidator(isExists)]);
    this.getErrorMessage('phone');
  }

  private checkValidOtpError(invalid: boolean) {
    this.setFieldsValidators([this.form.controls['otp']], [otpValidator(invalid)]);
    this.getErrorMessage('otp');
  }

  private setFieldsValidators(controls: AbstractControl[], validators: ValidatorFn[]): void {
    controls.map((control) => {
      control.setValidators(validators);
      control.updateValueAndValidity();
    });
  }
}
